import { useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import type { MemberRole } from '@/graphql/brokerages';
import { MEMBER_ROLES } from '@/lib/brokerages';

import styles from './MemberRoleForm.module.css';

export interface MemberRoleFormProps {
  /** Who is being changed, as the page names them. */
  member: string;
  current: string | null;
  /** How many extra roles they hold, which a change of main role removes. */
  extraRoleCount?: number;
  busy?: boolean;
  error?: string | null;
  onSubmit: (role: MemberRole) => void;
  onCancel: () => void;
}

/** Pick a member's main role. The main app picks the change up too. */
export function MemberRoleForm({
  member,
  current,
  extraRoleCount = 0,
  busy = false,
  error = null,
  onSubmit,
  onCancel,
}: MemberRoleFormProps) {
  const initial = (current ?? '').toUpperCase() as MemberRole;
  const [role, setRole] = useState<MemberRole>(initial);

  return (
    <Dialog
      actions={
        <>
          <Button disabled={busy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button disabled={role === initial} loading={busy} onClick={() => onSubmit(role)}>
            Save role
          </Button>
        </>
      }
      description="This changes their role in the main app too."
      dismissable={!busy}
      onClose={onCancel}
      title={`Role for ${member}`}
    >
      <div className={styles.form}>
        <fieldset className={styles.fieldset} disabled={busy}>
          <legend className={styles.legend}>Role</legend>
          {MEMBER_ROLES.map((option) => (
            <label
              className={`${styles.option} ${option.role === role ? styles.checked : ''}`}
              key={option.role}
            >
              <input
                checked={option.role === role}
                className={styles.input}
                name="member-role"
                onChange={() => setRole(option.role)}
                type="radio"
              />
              <span className={styles.text}>
                <span className={styles.name}>{option.name}</span>
                <span className={styles.summary}>{option.summary}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {extraRoleCount > 0 && role !== initial ? (
          <Callout title="Their extra roles will be removed">
            Changing someone’s main role takes away the{' '}
            {extraRoleCount === 1 ? 'extra role' : `${extraRoleCount} extra roles`} they hold. Add
            them back from Roles & permissions if they still need them.
          </Callout>
        ) : null}
        {error ? (
          <Callout tone="danger" title="That didn't work">
            {error}
          </Callout>
        ) : null}
      </div>
    </Dialog>
  );
}
